import { isUuid } from '@leanin/shared';
import { RecContextSchema } from '@leanin/shared/rec-context';
import type { GroupKind } from '@/lib/group-url';
import { readStorage, removeStorage, writeStorage } from '@/lib/browser-storage';
import type { RecContext } from '@/lib/recs/rec-context';

// The group a signed-out visitor asked to join before they had an account —
// written when they click "Sign in to Join" on a public group page, read
// once at /auth/finishing, which submits the join through the normal
// joinPolicy-dispatched endpoint (open → member, request → pending join
// request). sessionStorage so it survives the signup + OAuth round-trip in
// this tab (same posture as pendingInviteToken) and dies with the tab.
// Cleared at finishing on every signup so a stale intent can't join a later
// account into a group it never asked for.
//
// A click on a recommended directory card also carries the card's `rec`
// (the logged serve and position), so the join made at finishing is
// attributed to the anonymous serve the visitor clicked. Storage is a
// boundary: a `rec` that no longer parses is dropped and the join still runs.
const PENDING_JOIN_GROUP_KEY = 'auth.pendingJoinGroup';

export interface PendingJoinGroup {
  readonly kind: GroupKind;
  readonly id: string;
  readonly rec?: RecContext;
}

const GROUP_KINDS: ReadonlyArray<GroupKind> = ['circle', 'network', 'space'];

export function writePendingJoinGroup(group: PendingJoinGroup): void {
  if (typeof window === 'undefined') return;
  writeStorage('session', PENDING_JOIN_GROUP_KEY, JSON.stringify(group));
}

export function readPendingJoinGroup(): PendingJoinGroup | null {
  if (typeof window === 'undefined') return null;
  const raw = readStorage('session', PENDING_JOIN_GROUP_KEY);
  if (raw === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    if (!(err instanceof SyntaxError)) throw err;
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const candidate = parsed as { kind?: unknown; id?: unknown; rec?: unknown };
  if (
    typeof candidate.kind !== 'string' ||
    !(GROUP_KINDS as ReadonlyArray<string>).includes(candidate.kind) ||
    typeof candidate.id !== 'string' ||
    !isUuid(candidate.id)
  ) {
    return null;
  }
  const rec = RecContextSchema.safeParse(candidate.rec);
  return {
    kind: candidate.kind as GroupKind,
    id: candidate.id,
    ...(rec.success ? { rec: rec.data } : {}),
  };
}

// The rec context the post-signup join replays: the stored card's, and only
// when the join is for the group that card was clicked on — a join-link
// claim that outranks the stored intent is not attributed to its serve.
export function pendingJoinRecContext(
  pending: PendingJoinGroup | null,
  joinedGroupId: string,
): RecContext | undefined {
  return pending !== null && pending.id === joinedGroupId ? pending.rec : undefined;
}

export function clearPendingJoinGroup(): void {
  if (typeof window === 'undefined') return;
  removeStorage('session', PENDING_JOIN_GROUP_KEY);
}
